import { useIntersectionObserver, useResizeObserver } from '@vueuse/core';
import { computed, onUnmounted, ref, Ref } from 'vue';

type TimelineScrollShadowRefs = {
    /** The sticky location pane, which carries the horizontal shadow. */
    locationPaneRef: Ref<HTMLElement | null>;
    /** Zero-height probes at the top-left of the scroll container. */
    verticalScrollDetectorRef: Ref<HTMLElement | null>;
    horizontalScrollDetectorRef: Ref<HTMLElement | null>;
    /** The sticky first-column header, which carries the vertical shadow. */
    firstColumnHeaderRef: Ref<HTMLElement | null>;
    /** TimelineHeader's instance — it exposes its outer element as `ref`. */
    timelineHeaderRef: Ref<{ ref: HTMLElement | null } | null | undefined>;
};

/**
 * Toggles the timeline's scroll-shadow classes and measures the two sticky panes.
 *
 * The shadows are driven by intersection observers on zero-height probe elements rather than scroll
 * events, so a shadow only appears once there is actually content hidden behind the sticky pane.
 *
 * The returned sizes back the `v-bind()` CSS in Timeline.vue, which resolves setup-scope bindings by
 * name — keep the `headerHeight` / `locationPaneWidth` names when destructuring.
 */
export function useTimelineScrollShadows({
    locationPaneRef,
    verticalScrollDetectorRef,
    horizontalScrollDetectorRef,
    firstColumnHeaderRef,
    timelineHeaderRef,
}: TimelineScrollShadowRefs) {
    const timelineHeaderElement = computed(() => timelineHeaderRef.value?.ref ?? null);

    const { stop: stopVerticalScrollDetector } = useIntersectionObserver(
        verticalScrollDetectorRef,
        ([args]) => {
            if (args.isIntersecting) {
                firstColumnHeaderRef.value?.classList.remove('scroll-shadow-vertical');
                timelineHeaderElement.value?.classList.remove('scroll-shadow-vertical');
            } else {
                firstColumnHeaderRef.value?.classList.add('scroll-shadow-vertical');
                timelineHeaderElement.value?.classList.add('scroll-shadow-vertical');
            }
        },
        {},
    );

    const { stop: stopHorizontalScrollDetector } = useIntersectionObserver(
        horizontalScrollDetectorRef,
        ([args]) => {
            if (args.isIntersecting) {
                locationPaneRef.value?.classList.remove('scroll-shadow-horizontal');
            } else {
                locationPaneRef.value?.classList.add('scroll-shadow-horizontal');
            }
        },
        {},
    );

    onUnmounted(() => {
        stopVerticalScrollDetector();
        stopHorizontalScrollDetector();
    });

    /**
     * Measured straight off the ResizeObserver entry rather than with `useElementSize`.
     *
     * `useElementSize`'s mount hook reads `getComputedStyle()` and `offsetWidth`/`offsetHeight` to derive
     * the content box synchronously, which forces a style recalc and a layout. The timeline's DOM is tens
     * of thousands of nodes by the time these mount, so those two reads cost ~80ms of the mount together.
     *
     * `contentRect` is already the content box — padding and border excluded — so this yields exactly the
     * numbers `useElementSize` produced, and the observer delivers them after layout but before paint, so
     * the CSS variables below are still set for the first frame without forcing anything.
     */
    const headerHeightInPx = ref(0);
    const locationPaneWidthInPx = ref(0);

    useResizeObserver(timelineHeaderElement, ([entry]) => {
        headerHeightInPx.value = entry.contentRect.height;
    });

    useResizeObserver(locationPaneRef, ([entry]) => {
        locationPaneWidthInPx.value = entry.contentRect.width;
    });

    return {
        headerHeight: computed(() => `${headerHeightInPx.value}px`),
        locationPaneWidth: computed(() => `${locationPaneWidthInPx.value}px`),
    };
}
